import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, MapPin, Filter, X, ChevronLeft, ChevronRight} from 'lucide-react';
import useDebounce from '../hooks/useDebounce';
import ServiceCard from '../components/ServiceCard';
import ServiceDetailModal from '../components/ServiceDetailModal';
import { getServices } from '../services/api';
import './BrowseServices.css';


const CATEGORIES = [
  'B2B Cab & Transport',
  'B2B Hotel & Homestay',
  'Tour Operator',
  'Local Guide',
  'Activities & Experiences',
  'DMC',
  'Visa Assistance',
  'B2B Group Tour Service',
  'B2B Travel Package',
  'International Tour Package',
  'Flight Booking Services',
  'Other'
];

const ITEMS_PER_PAGE = 4;

const BrowseServices = ({ onPostService }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Filter states
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [locationFilter, setLocationFilter] = useState(searchParams.get('location') || '');
  const [selectedCategories, setSelectedCategories] = useState(() => {
    const category = searchParams.get('category');
    return category ? [category] : [];
  });

  // Debounced values
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const debouncedLocationFilter = useDebounce(locationFilter, 500);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        search: debouncedSearchQuery,
        location: debouncedLocationFilter,
        category: selectedCategories.length === 1 ? selectedCategories[0] : undefined,
        page: currentPage,
        limit: ITEMS_PER_PAGE
      };

      const response = await getServices(params);
      setServices(response.services);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchQuery, debouncedLocationFilter, selectedCategories, currentPage]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  useEffect(() => {
    // Update URL params
    const params = new URLSearchParams();
    if (debouncedSearchQuery) params.set('search', debouncedSearchQuery);
    if (debouncedLocationFilter) params.set('location', debouncedLocationFilter);
    if (selectedCategories.length === 1) params.set('category', selectedCategories[0]);
    // We optionally, could add page to URL here, but keeping it simple for now as requested
    setSearchParams(params, { replace: true });
  }, [debouncedSearchQuery, debouncedLocationFilter, selectedCategories, setSearchParams]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setLocationFilter('');
    setSelectedCategories([]);
    setCurrentPage(1);
  };

  const hasActiveFilters = searchQuery || locationFilter || selectedCategories.length > 0;

  const filteredServices = services.filter(service => {
    if (selectedCategories.length > 1) {
      return selectedCategories.includes(service.category);
    }
    return true;
  });

  return (
    <div className="browse-page">
      <div className="container browse-container">
        {/* Mobile Filter Toggle */}
        <button
          className="mobile-filter-toggle"
          onClick={() => setShowMobileFilters(true)}
        >
          <Filter size={20} />
          Filters
          {hasActiveFilters && <span className="filter-badge"></span>}
        </button>

        {/* Sidebar Filters */}
        <aside className={`filters-sidebar ${showMobileFilters ? 'filters-open' : ''}`}>
          <div className="filters-header-mobile">
            <h3>Filters</h3>
            <button
              className="filters-close-btn"
              onClick={() => setShowMobileFilters(false)}
            >
              <X size={24} />
            </button>
          </div>

          <div className="filters-content">
            <div className="filter-section">
              <h3 className="filter-title">
                <MapPin size={18} />
                Location
              </h3>
              <input
                type="text"
                placeholder="Filter by City or State"
                value={locationFilter}
                onChange={(e) => {
                  setLocationFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="form-input filter-input"
              />
            </div>

            <div className="filter-divider"></div>

            <div className="filter-section">
              <h3 className="filter-title">Categories</h3>
              <div className="category-filters-dropdown">
                <select
                  className="form-select filter-input"
                  value={selectedCategories[0] || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCurrentPage(1);
                    setSelectedCategories(val ? [val] : []);
                  }}
                >
                  <option value="">All Categories</option>
                  {CATEGORIES.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            </div>

            {hasActiveFilters && (
              <button className="clear-filters-btn" onClick={clearAllFilters}>
                Clear all filters
              </button>
            )}
          </div>

          <div className="filters-footer-mobile">
            <button
              className="btn btn-primary"
              onClick={() => setShowMobileFilters(false)}
            >
              Show Results ({filteredServices.length})
            </button>
          </div>
        </aside>

        {/* Mobile Filter Overlay */}
        {showMobileFilters && (
          <div
            className="filters-overlay"
            onClick={() => setShowMobileFilters(false)}
          ></div>
        )}

        <main className="browse-main">
          {/* Search Bar */}
          <div className="browse-search-container">
            <div className="browse-search">
              <Search className="browse-search-icon" size={20} />
              <input
                type="text"
                placeholder="Search services, providers..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="browse-search-input"
              />
              {searchQuery && (
                <button
                  className="search-clear-btn"
                  onClick={() => {
                    setSearchQuery('');
                    setCurrentPage(1);
                  }}
                >
                  <X size={18} />
                </button>
              )}
            </div>
            <button className="mobile-post-service-btn" onClick={onPostService}>Post Service</button>
          </div>

          {/* Results */}
          <div className="browse-results">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading services...</p>
              </div>
            ) : filteredServices.length > 0 ? (
              <div className="services-grid">
                {filteredServices.map(service => (
                  <ServiceCard
                    key={service._id}
                    service={service}
                    onClick={() => setSelectedService(service)}
                  />
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">
                  <Search size={48} strokeWidth={1} />
                </div>
                <h3>No services found</h3>
                <p>Try adjusting your filters or search terms.</p>
                {hasActiveFilters && (
                  <button className="btn btn-secondary" onClick={clearAllFilters}>
                    Clear all filters
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Pagination */}
          {!loading && pagination && (
            <>
              <div className="pagination-info">
                Showing {((pagination.currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(pagination.currentPage * ITEMS_PER_PAGE, pagination.totalServices)} of {pagination.totalServices} services
              </div>

              {pagination.totalPages > 1 && (
                <div className="pagination-container">
                  <button
                    className="pagination-btn"
                    disabled={pagination.currentPage === 1}
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                  >
                    <ChevronLeft />
                  </button>

                  {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      // Show first, last, current, and surrounding pages
                      return (
                        page === 1 ||
                        page === pagination.totalPages ||
                        Math.abs(page - pagination.currentPage) <= 1
                      );
                    })
                    .map((page, index, array) => {
                      // Add ellipses logic if needed, but for simplicity just verifying continuity
                      const prevPage = array[index - 1];
                      const showEllipsis = prevPage && page - prevPage > 1;

                      return (
                        <React.Fragment key={page}>
                          {showEllipsis && <span className="pagination-ellipsis">...</span>}
                          <button
                            className={`pagination-btn ${pagination.currentPage === page ? 'active' : ''}`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      );
                    })}

                  <button
                    className="pagination-btn"
                    disabled={pagination.currentPage === pagination.totalPages}
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                  >
                    <ChevronRight />
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Service Detail Modal */}
      {selectedService && (
        <ServiceDetailModal
          service={selectedService}
          onClose={() => setSelectedService(null)}
        />
      )}
    </div>
  );
};

export default BrowseServices;

